import BaseService from '../base-service';
import StandardResponse from '../../models/standard-response';
import {
    ICPOInterfacesList,
    ICPOInterfaces,
    ICPOInterfacesData,
    IOCPIList,
    IOCPIRequest,
    ICPOInterfacesRequest,
    ICPOEndpointsList,
    IVersionEndpointRequest,
    ICPOType,
} from 'models/cpo-interface';
import { IPaginatedTariff, IPagingTariff } from 'models/common-models';

export default class CPOInterfaceService {
    getCPOsList = async (data: ICPOInterfacesRequest): Promise<StandardResponse<ICPOInterfacesList>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos/search`,
                method: 'POST',
                data,
            });
            return new StandardResponse<ICPOInterfacesList>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    getOCPIsList = async (data: IOCPIRequest): Promise<StandardResponse<IOCPIList>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/ocpi-versions/search`,
                method: 'POST',
                data,
            });
            return new StandardResponse<IOCPIList>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    getCPOById = async (id: number): Promise<StandardResponse<ICPOInterfaces>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos/${id}`,
                method: 'GET',
            });
            return new StandardResponse<ICPOInterfaces>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    addCPO = async (data: ICPOInterfacesData): Promise<StandardResponse<ICPOInterfaces>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos`,
                method: 'POST',
                data,
            });
            return new StandardResponse<ICPOInterfaces>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    editCPO = async (data: ICPOInterfacesData, id: number): Promise<StandardResponse<number>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos/${id}`,
                method: 'PUT',
                data,
            });
            return new StandardResponse<number>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
    updateMutualOCPIVersion = async (mutual_ocpi_version: string, id: number): Promise<StandardResponse<number>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_OCPI_API_URL,
                url: `/ocpi/emsp/${mutual_ocpi_version}/admin/handshake/${id}`,
                method: 'PUT',
            });
            return new StandardResponse<number>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    updateCPOStatus = async (id: number, data: { active: boolean }): Promise<StandardResponse<number>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos/${id}`,
                method: 'patch',
                data,
            });
            return new StandardResponse<number>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    performHandshake = async (
        id: number,
        mutual_ocpi_version: string,
        data: any,
    ): Promise<StandardResponse<boolean>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_OCPI_API_URL,
                url: `/ocpi/emsp/${mutual_ocpi_version}/admin/handshake/${id}`,
                method: 'POST',
                data,
            });
            return new StandardResponse<boolean>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    getEndpointsById = async (data: IVersionEndpointRequest): Promise<StandardResponse<ICPOEndpointsList>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos/endpoints/search`,
                method: 'POST',
                data,
            });
            return new StandardResponse<ICPOEndpointsList>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    saveHandshakeToken = async (
        data: { handshake_token: string },
        id: number,
        regenerate: boolean,
    ): Promise<StandardResponse<number>> => {
        const url = regenerate ? 'save-regenerate-handshake-token' : 'save-handshake-token';
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `v1/admin/cpos/${url}/${id}`,
                method: 'PUT',
                data,
            });
            return new StandardResponse<number>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    getCPOTypeList = async (data: IOCPIRequest): Promise<StandardResponse<ICPOType>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos/cpo-types/search`,
                method: 'POST',
                data,
            });
            return new StandardResponse<ICPOType>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };

    getOverrideTariff = async (data: IPagingTariff): Promise<StandardResponse<IPaginatedTariff>> => {
        try {
            const res = await BaseService({
                baseURL: process.env.REACT_APP_EMSP_ADMIN_API_URL,
                url: `/v1/admin/cpos/assigned-tariffs/search`,
                method: 'POST',
                data,
            });
            return new StandardResponse<IPaginatedTariff>(res.status, res.data.data, res.data.message, null);
        } catch (e) {
            return Promise.reject(e);
        }
    };
}
